import { useApi } from '@shared/api'
import type {
  Institution,
  Branch,
  BranchTable,
  FrontPadSettings,
  EmailSettings,
  TelegramSettings,
  MaxSettings,
  VkSettings,
  VisualSettings,
  YandexSettings,
  NewsSliderItem,
  LegalSettings,
  SeoSettings,
  TaxSettings,
  ConsentSettings,
} from '@entities/institution'

export const apiAdminInstitution = {
  async getInstitutionWithBranches(id: number): Promise<{ institution: Institution; branches: Branch[] }> {
    const { data } = await useApi(true, true).get(`/admin/institutions/${id}/branches`)
    return data
  },

  async updateInstitution(id: number, payload: Omit<Institution, 'id'>): Promise<Institution> {
    const { data } = await useApi(true, true).patch(`/admin/institutions/${id}`, { body: payload })
    return data
  },

  async updateBranch(branchId: string | number, payload: Partial<Branch>): Promise<Branch> {
    const { data } = await useApi(true, true).patch(`/admin/institutions/branches/${branchId}`, { body: payload })
    return data
  },

  async disableBranch(branchId: string | number): Promise<{ message: string }> {
    const { data } = await useApi(true, true).patch(`/admin/institutions/branches/${branchId}/disable`)
    return data
  },

  async enableBranch(branchId: string | number): Promise<{ message: string }> {
    const { data } = await useApi(true, true).patch(`/admin/institutions/branches/${branchId}/enable`)
    return data
  },

  async applyPreorderToAllBranches(branchId: string | number): Promise<{ success: boolean; updated_count: number }> {
    const { data } = await useApi(true, true).post(`/admin/institutions/branches/${branchId}/preorder/apply-to-all`)
    return data
  },

  async updateBranchTables(branchId: string | number, tables: BranchTable[]): Promise<BranchTable[]> {
    const { data } = await useApi(true, true).put(`/admin/institutions/branches/${branchId}/tables`, { body: tables })
    return data
  },

  async deleteTable(branchId: string | number, tableId: number): Promise<{ message: string }> {
    const { data } = await useApi(true, true).delete(`/admin/institutions/branches/${branchId}/tables/${tableId}`)
    return data
  },

  async getFrontPadSettings(): Promise<{
    success: boolean
    frontpad: FrontPadSettings | null
    catalog: import('@entities/institution').FrontPadCatalog
  }> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/frontpad')
    return data
  },

  async updateFrontPadSettings(payload: FrontPadSettings): Promise<FrontPadSettings> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/frontpad', { body: payload })
    return data
  },

  async getPaymentSettings(): Promise<any> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/payment')
    return data
  },

  async updateYookassaSettings(payload: {
    yookassa_enabled?: boolean
    is_test?: boolean
    yookassa_shop_id?: string | null
    yookassa_shop_id_test?: string | null
  }): Promise<{ success: boolean }> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/yookassa', { body: payload })
    return data
  },

  async updateTbusinessSettings(payload: {
    tbusiness_enabled?: boolean
    is_test?: boolean
    tbusiness_terminal_key?: string | null
    tbusiness_terminal_key_test?: string | null
  }): Promise<{ success: boolean }> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/tbusiness', { body: payload })
    return data
  },

  async updateAlfabankSettings(payload: {
    is_active?: boolean
    is_test?: boolean
    username?: string | null
    password?: string | null
    username_test?: string | null
    password_test?: string | null
    token?: string | null
    token_test?: string | null
    /**
     * Открытый (клиентский) токен мерчанта для кнопки Alfa Pay в приложении.
     * Выдаётся поддержкой банка отдельно и НЕ совпадает с `token`: этот можно отдавать
     * на клиент, тот — технический. Тестового контура у Alfa Pay нет, поле одно.
     */
    public_token?: string | null
    webhook_secret?: string | null
  }): Promise<{ success: boolean }> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/alfabank', { body: payload })
    return data
  },

  async getEmailSettings(): Promise<{ success: boolean; email: EmailSettings }> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/email')
    return data
  },

  async updateEmailSettings(payload: EmailSettings): Promise<EmailSettings> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/email', { body: payload })
    return data
  },

  async getTelegramSettings(): Promise<any> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/telegram')
    return data
  },

  async updateTelegramSettings(payload: TelegramSettings): Promise<TelegramSettings> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/telegram', { body: payload })
    return data
  },

  /** Вкл/выкл авторизацию через Telegram (institution_telegram.auth_enabled). */
  async updateTelegramAuthEnabled(enabled: boolean): Promise<{ success: boolean }> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/telegram-auth', { body: { enabled } })
    return data
  },

  /** Приостановить/возобновить приём оплаты и заказов (institution_settings.payment_paused). */
  async updatePaymentPause(payload: { paused: boolean }): Promise<{ success: boolean }> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/payment-pause', { body: payload })
    return data
  },

  /**
   * Реквизиты кассового чека (54-ФЗ): система налогообложения и ставка НДС.
   * До появления настройки во всех чеках были захардкожены ОСН и НДС 20%.
   */
  async getTaxSettings(): Promise<TaxSettings> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/tax')
    return data
  },

  async updateTaxSettings(payload: TaxSettings): Promise<{ success: boolean }> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/tax', { body: payload })
    return data
  },

  /**
   * Обязательность галочки согласия на рекламу в форме входа.
   * Выведено в админку, чтобы требование можно было снять без выката —
   * юридическую оговорку см. в типе ConsentSettings.
   */
  async getConsentSettings(): Promise<ConsentSettings> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/consent')
    return data
  },

  async updateConsentSettings(payload: ConsentSettings): Promise<{ success: boolean }> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/consent', {
      body: payload,
    })
    return data
  },

  async getMaxSettings(): Promise<{ success: boolean; max: MaxSettings | null }> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/max')
    return data
  },

  async updateMaxSettings(payload: MaxSettings): Promise<{ success: boolean; message: string }> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/max', { body: payload })
    return data
  },

  async getVkSettings(): Promise<{ success: boolean; vk: VkSettings | null }> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/vk')
    return data
  },

  async updateVkSettings(payload: VkSettings): Promise<{ success: boolean; message: string }> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/vk', { body: payload })
    return data
  },

  async getVisualSettings(): Promise<{ success: boolean; visual: VisualSettings }> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/visual')
    return data
  },

  async updateVisualSettings(payload: VisualSettings): Promise<VisualSettings> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/visual', { body: payload })
    return data
  },

  /**
   * Загрузка одного файла для раздела «Внешний вид» (видео обложки, картинки
   * контентных страниц). Бэкенд кладёт файл в S3 и возвращает публичный URL —
   * сам URL уезжает в params_json вместе с остальным конфигом, обычным сохранением.
   */
  async uploadAppearanceMedia(file: File): Promise<{ success: boolean; url: string }> {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await useApi(true, true).post('/admin/institutions/settings/appearance-media', { body: formData })
    return data
  },

  /**
   * Загрузка картинок промо-блока приложения (телефон-макет + QR) в S3.
   * Возвращает публичные URL — их затем сохраняем в visual.params_json.
   */
  async uploadAppPromoMedia(
    payload: { phone_image?: File | null; qr_image?: File | null; phone_image_url?: string; qr_image_url?: string },
  ): Promise<{ success: boolean; web_app_promo_phone_image_url?: string | null; web_app_install_qr_image_url?: string | null }> {
    const formData = new FormData()
    if (payload.phone_image) formData.append('phone_image', payload.phone_image)
    if (payload.qr_image) formData.append('qr_image', payload.qr_image)
    // Текущие URL передаём всегда (пустая строка = очистить, файл переопределяет)
    formData.append('phone_image_url', payload.phone_image_url ?? '')
    formData.append('qr_image_url', payload.qr_image_url ?? '')
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/app-promo-media', { body: formData })
    return data
  },

  async getYandexSettings(): Promise<{ success: boolean; yandex: YandexSettings }> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/yandex')
    return data
  },

  async updateYandexSettings(payload: YandexSettings): Promise<YandexSettings> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/yandex', { body: payload })
    return data
  },

  /** Вкл/выкл авторизацию через Yandex (institution_yandex.is_active). */
  async updateYandexAuthEnabled(enabled: boolean): Promise<{ success: boolean }> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/yandex-auth', { body: { enabled } })
    return data
  },

  async getNewsSliderSettings(): Promise<{ success: boolean; sliders: NewsSliderItem[] }> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/news-slider')
    return data
  },

  async getLegalSettings(): Promise<{ success: boolean; legal: LegalSettings }> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/legal')
    return data
  },

  async updateLegalSettings(payload: LegalSettings): Promise<LegalSettings> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/legal', { body: payload })
    return data
  },

  async getSeoSettings(): Promise<{ success: boolean; seo: SeoSettings | null }> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/seo')
    return data
  },

  async updateSeoSettings(payload: SeoSettings): Promise<{ success: boolean; seo: SeoSettings }> {
    const { data } = await useApi(true, true).patch('/admin/institutions/settings/seo', { body: payload })
    return data
  },

  async getBranchesSettings(): Promise<Branch[]> {
    const { data } = await useApi(true, true).get('/admin/institutions/settings/branches')
    return data.branches
  },

  async createBranch(payload: Omit<Branch, 'id' | 'is_active'>): Promise<Branch> {
    const { data } = await useApi(true, true).post('/admin/institutions/branches', { body: payload })
    return data
  },

  async deleteBranch(branchId: string | number): Promise<{ message: string }> {
    const { data } = await useApi(true, true).delete(`/admin/institutions/branches/${branchId}`)
    return data
  },

  async syncProductsFromFrontPad(institutionId: number): Promise<any> {
    const { data } = await useApi(true, true).get(`/frontpad/${institutionId}/products`)
    return data
  },

  async syncStopListFromFrontPad(institutionId: number): Promise<{ success: boolean; message: string; error?: string }> {
    const { data } = await useApi(true, true).get(`/frontpad/${institutionId}/stop-list`)
    return data
  },
}

export default apiAdminInstitution
